import type { components, operations } from '@/services/clients/editorApi/_generated/api';
import type { ServiceConfig } from '@/services/httpServices/types';

// ------------- Activity Types from API -------------

export type UserActivityListResponse = components['schemas']['UserActivityListResponse'];
export type RunningResourceActivitiesResponse = components['schemas']['RunningResourceActivitiesResponse'];

/**
 * Single activity from the API response
 * Derived directly from the generated API types
 */
export type ApiActivity = UserActivityListResponse['nodes'][number];

/**
 * Activity service types derived from the API
 * This ensures frontend and backend stay in sync automatically
 */
export type ApiActivityService = ApiActivity['service'];

/**
 * Activity kind types derived from the API
 * This ensures frontend and backend stay in sync automatically
 */
export type ApiActivityKind = ApiActivity['kind'];

/**
 * Activity API status types from the meta field
 */
export type ApiActivityStatus = ApiActivity['meta']['status'];

/**
 * All possible service values as a const array
 * Used for validation schemas and iteration
 */
export const API_ACTIVITY_SERVICES = [
  'TRANSLATION_SERVICE',
  'VOICE_SERVICE',
  'COPY_SERVICE',
  'PUBLISH_SERVICE',
  'DOC2COURSE',
] as const satisfies readonly ApiActivityService[];

/**
 * All possible kind values as a const array
 * Used for validation schemas and iteration
 */
export const API_ACTIVITY_KINDS = [
  // Translation kinds
  'TRANSLATE_SEQUENCE',
  'TRANSLATE_QUIZ',
  'TRANSLATE_COURSE',
  // Copy kinds
  'COPY_ALL_COURSES',
  'COPY_SELECTED_COURSES',
  'COPY_SINGLE_COURSE',
  'COPY_SINGLE_QUIZ',
  'COPY_SELECTED_QUIZZES',
  'COPY_SINGLE_SEQUENCE',
  'COPY_SELECTED_SEQUENCES',
  'COPY_SINGLE_CHECKLIST',
  // Voice kinds
  'SYNC_HOW_FM_VOICES',
  'SYNC_SPACE_VOICES_TO_SEQUENCES',
  // Publish kinds
  'PUBLISH_SEQUENCE',
  'PUBLISH_QUIZ',
  'PUBLISH_COURSE',
  // Doc2Course kinds
  'GENERATE_COURSE_STRUCTURE',
  'GENERATE_COURSE',
] as const satisfies readonly ApiActivityKind[];

/**
 * Activity API status values
 */
export const API_ACTIVITY_STATUSES = [
  'inProgress',
  'done',
  'error',
  'retriggered',
] as const satisfies readonly ApiActivityStatus[];

// ------------- Query Parameters -------------

export type ActivityListQueryParams = operations['getUserActivitiesList']['parameters']['query'];
export type ActivityListPathParams = operations['getUserActivitiesList']['parameters']['path'];

export type RunningResourceActivitiesQueryParams = operations['getRunningResourceActivities']['parameters']['query'];
export type RunningResourceActivitiesPathParams = operations['getRunningResourceActivities']['parameters']['path'];

// ------------- Service Configs -------------

export type GetActivityListServiceConfig = ServiceConfig<{
  params: ActivityListPathParams;
  query?: Partial<ActivityListQueryParams>;
}>;

export type GetRunningResourceActivitiesServiceConfig = ServiceConfig<{
  params: RunningResourceActivitiesPathParams;
  query: RunningResourceActivitiesQueryParams;
}>;
